<template>
  <div class="flex align-center">
    <el-autocomplete
      v-model="state"
      clearable
      :fetch-suggestions="querySearchAsync"
      placeholder="Search Member"
      :trigger-on-focus="true"
      minlength="3"
      class="w-[240px]"
      popper-class="swd-remote-search-popper"
      @select="handleSelect"
    >
      <template #prefix>
        <div class="flex items-center">
          <el-icon>
            <Search />
          </el-icon>
        </div>
      </template>
      <template #default="{ item }">
        <div class="value font-semibold">{{ item.name }}</div>
        <div v-if="item.email || item.phone" class="text-xs">
          <span>{{ item.email }}</span>
          <span class="ml-3">{{ item.phone }}</span>
        </div>
      </template>
    </el-autocomplete>
  </div>
</template>

<script>
import { Search } from '@element-plus/icons-vue'
import { defineComponent, ref, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'

import store from '../../store'

import { useSearchRemoteMembers } from '@/api/use-search-remote-members.js'

export default defineComponent({
  name: 'SwdRemoteSearch',
  components: {
    Search,
  },
  setup() {
    const router = useRouter()
    const route = useRoute()
    const links = ref([])
    const state = ref('')

    const { isLoading, isError, data, refetch } = useSearchRemoteMembers(
      {
        search: state,
      },
      { enabled: false }
    )

    watch(state, (val) => {
      if (typeof val !== 'string') {
        return
      }

      let cleanVal = val

      // If it contains @, treat it as an email and preserve email-valid characters
      if (cleanVal.includes('@')) {
        cleanVal = cleanVal.replace(/[^a-zA-Z0-9@.+_-]/g, '')
      } else {
        // First, remove clearly invalid characters (keep letters, numbers, and common separators)
        cleanVal = cleanVal.replace(/[^a-zA-Z0-9+.@_&\-() ]/g, '')

        // If the string contains any numbers, remove all special characters
        // This handles phone numbers, numeric codes, and alphanumeric IDs
        if (/\d/.test(cleanVal)) {
          cleanVal = cleanVal.replace(/[^a-zA-Z0-9]/g, '')
        } else {
          // If it's text-only (like names), keep hyphens and underscores but remove other special chars
          cleanVal = cleanVal.replace(/[^a-zA-Z\-_@&.+ ]/g, '')
        }
      }

      // Remove leading spaces
      cleanVal = cleanVal.trimStart()

      state.value = cleanVal
    })
    const querySearchAsync = async (_, callback) => {
      const query = (state.value ?? '').trimStart()
      state.value = query
      if (query.length > 2) {
        refetch
          .value()
          .then(
            (res) =>
              res.data?.map((item) => ({
                id: item.id,
                name: item.name?.trimStart(),
                email: item.email || '',
                phone: item.phone || '', // Placeholder, replace with actual phone if available
              })) ?? []
          )
          .then((data) => callback(data.length ? data : [{ name: 'Not Found' }]))
      } else {
        callback([]) // Return empty array if query is too short
      }
    }
    const handleSelect = (item) => {
      const metaType = route.meta.type || store.state.globalComponents.role
      const features = [
        { destination: 'tasksQueue', value: 'taskList' },
        { destination: 'giftReferrals', value: 'client_gifts' },
        { destination: 'FinancialDetails', value: 'Opportunities' },
        { destination: 'communicationDetails', value: 'activity_details' },
      ]
      // Loop through features and update the store for each destination/value pair
      features.forEach(({ destination, value }) => {
        store.commit('globalComponents/setActiveTabs', {
          destination,
          value,
        })
      })
      if (item.id) {
        router.push({ name: `${metaType}/member-details`, params: { id: item.id } })
      }
    }

    return {
      links,
      state,
      querySearchAsync,
      handleSelect,
      isLoading,
      isError,
      data,
    }
  },
})
</script>
<style>
.swd-remote-search-popper li {
  color: #000;
  padding: 6px 20px;
  line-height: 24px;
}
</style>
